/*
 *   File : external-investment.js
 *   Author : https://evoqins.com
 *   Description : external investment component (API integrated)
 *   Integrations : user/list-external-portfolio
 *   Version : 1.1.0
 */
import React, { useEffect, useRef, useState } from "react";
import { DataTableComponent } from "../../../Components/DataTable";
import { EmptyScreen, Loader } from "../../../Components/Other";
import { APIService } from "../../../Service";

export default function ExternalInvestments({
  id,
  setFilterData,
  btnService,
  setFilterType,
}) {
  const [loader, setLoader] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [isEmpty, setIsEmpty] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  const listFetchIdRef = useRef(0);

  useEffect(() => {
    setFilterData({});
    setFilterType(3);
    btnService(console.log("btn clicked"));
  }, []);

  useEffect(() => {
    if (id === undefined || id === null) {
      setLoader(false);
      return;
    }
    _getExternalPortfolio();
  }, [id, page, perPage]);

  const _getExternalPortfolio = () => {
    const fetchId = ++listFetchIdRef.current;
    setLoader(true);

    const payload = {
      customer_id: id,
      page_num: page,
      page_size: perPage,
    };

    APIService(true, "user/list-external-portfolio", payload)
      .then((res) => {
        if (fetchId !== listFetchIdRef.current) return;
        if (res.status_code === 200) {
          const records = res.data?.data || [];
          setTableData(records);
          setTotalRows(res.data?.total_count || records.length);
          setIsEmpty(records.length === 0);
        } else {
          setTableData([]);
          setIsEmpty(true);
        }
      })
      .catch((err) => {
        if (fetchId !== listFetchIdRef.current) return;
        console.error("External portfolio error:", err);
        setTableData([]);
        setIsEmpty(true);
      })
      .finally(() => {
        if (fetchId === listFetchIdRef.current) {
          setLoader(false);
        }
      });
  };

  const COLUMNS = [
    {
      name: "Name",
      selector: (row) => row.fund_name,
      wrap: true,
      width: "260px",
      cell: (row) => (
        <div className="d-flex column-gap-2 align-items-center">
          <img
            src={row.fund_image}
            alt={row.fund_name}
            style={{
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              objectFit: "cover",
            }}
          />
          <span>{row.fund_name}</span>
        </div>
      ),
    },
    {
      name: "Units",
      selector: (row) => row.units,
      width: "100px",
    },
    {
      name: "Purchase NAV",
      selector: (row) => row.purchase_nav,
      width: "140px",
    },
    {
      name: "Invested",
      selector: (row) => row.invested_amount,
      width: "150px",
    },
    {
      name: "Current",
      selector: (row) => row.current_amount,
      width: "150px",
    },
    {
      name: "Returns",
      selector: (row) => row.return_text,
      width: "180px",
      cell: (row) => (
        <p
          className={`mb-0 ${
            row.return < 0 ? "text-pure-red" : "text-medium-sea-green"
          }`}
        >
          {row.return_text}{" "}
          <span className="fs-12">({row.return}%)</span>
        </p>
      ),
    },
    {
      name: "1D Returns",
      selector: (row) => row.one_day_returns,
      width: "180px",
      cell: (row) => (
        <p
          className={`mb-0 ${
            row.one_day_returns < 0 ? "text-pure-red" : "text-medium-sea-green"
          }`}
        >
          {row.one_day_returns_text}{" "}
          <span className="fs-12">({row.one_day_returns_percentage_text})</span>
        </p>
      ),
    },
    {
      name: "XIRR",
      selector: (row) => row.xirr,
      width: "120px",
      cell: (row) => (
        <p
          className={`mb-0 ${
            row.xirr?.includes("-") ? "text-pure-red" : "text-medium-sea-green"
          }`}
        >
          {row.xirr}
        </p>
      ),
    },
  ];

  return (
    <div className="pt-1 row-gap-3 rounded-16 d-flex flex-column">
      {loader ? (
        <Loader />
      ) : isEmpty ? (
        <EmptyScreen message={"No External Investments Found"} />
      ) : (
        <DataTableComponent
          info={false}
          columns={COLUMNS}
          data={tableData}
          pagination={true}
          paginationServer={true}
          currentPage={page}
          paginationTotalRows={totalRows}
          paginationPerPage={perPage}
          onChangePage={(p) => setPage(p)}
          onChangeRowsPerPage={(newPerPage) => {
            setPerPage(newPerPage);
            setPage(1);
          }}
          className="e-table-basic"
        />
      )}
    </div>
  );
}